import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getApi } from '../../../Services/apiService';

const DetailRow = ({ label, value }) => (
    <div className="user-detail-row">
        <div className="user-detail-icon-wrap">
            <img src={window.getPublicUrl('/assets/images/email.svg')} alt="" className="user-detail-icon-img" width="24" height="24" />
        </div>
        <div className="user-detail-meta">
            <span className="user-detail-label">{label}</span>
            <span className="user-detail-value">{value || '-'}</span>
        </div>
    </div>
);

const PetDetailRow = ({ label, value }) => (
    <div className="pet-detail-row">
        <span className="pet-detail-label">{label}:</span>
        <span className="pet-detail-value">{value || '-'}</span>
    </div>
);

const UserDetail = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchUser = async () => {
            if (!userId) return;
            setLoading(true);
            setError(null);
            try {
                const response = await getApi(`/api/v1/admin/users/${userId}`);
                const data = response?.data ?? response;
                setUser(data);
            } catch (err) {
                console.error('Error fetching user details:', err);
                setError('Failed to load user details.');
                setUser(null);
            } finally {
                setLoading(false);
            }
        };
        fetchUser();
    }, [userId]);

    const formatDate = (val) => {
        if (!val) return '-';
        const d = typeof val === 'string' && !val.match(/^\d{4}-\d{2}-\d{2}/) ? val : new Date(val);
        return d instanceof Date && !isNaN(d) ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : val;
    };

    if (loading) {
        return (
            <div className="RightSideDiv">
                <div style={{ padding: '60px 20px', textAlign: 'center' }}>
                    <p>Loading user details...</p>
                </div>
            </div>
        );
    }

    if (error || !user) {
        return (
            <div className="RightSideDiv">
                <div style={{ padding: '60px 20px', textAlign: 'center' }}>
                    <p>{error || 'User not found.'}</p>
                    <button type="button" className="add-shelter-btn" style={{ marginTop: 16 }} onClick={() => navigate('/admin/users')}>
                        Back to User Management
                    </button>
                </div>
            </div>
        );
    }

    const name = user.name ?? user.fullName ?? 'User';
    const role = user.type ?? user.userType ?? user.role ?? '-';
    const isActive = user.status?.toLowerCase() === 'active' || user.isActive === true;
    const isPremium = user.isPremium === true || user.premium === true || (user.subscription && user.subscription !== 'free');
    const profileSrc = user.profile ?? user.profileImage ?? user.avatar ?? user.logo;
    const pets = Array.isArray(user.pets) ? user.pets : [];

    return (
        <div className="RightSideDiv">
            <div className="user-detail-layout">
                <div className="user-detail-card">
                    <div className="user-detail-avatar-wrap">
                        <img
                            src={profileSrc?.startsWith?.('http') ? profileSrc : (profileSrc ? window.getPublicUrl(profileSrc) : window.getPublicUrl('/assets/images/dummy-profile.jpg'))}
                            alt={name}
                            className="user-detail-avatar"
                            onError={(e) => { e.target.src = window.getPublicUrl('/assets/images/dummy-profile.jpg'); }}
                        />
                    </div>
                    <h2 className="user-detail-name">{name}</h2>
                    <p className="user-detail-role">{role}</p>
                    <div className="user-detail-badges">
                        <span className={`status-badge ${isActive ? 'active' : 'inactive'}`}>
                            {user.status ?? (isActive ? 'Active' : 'Inactive')}
                        </span>
                        {isPremium && (
                            <span className="status-badge premium-badge">Premium</span>
                        )}
                    </div>
                    <div className="user-detail-meta-list">
                        <DetailRow label="Email" value={user.email} />
                        <DetailRow label="Phone" value={user.phone} />
                        <DetailRow label="City" value={user.city ?? user.address} />
                        <DetailRow label="Joined" value={user.joinedDate ?? formatDate(user.dateJoined ?? user.createdAt)} />
                    </div>
                </div>

                <div className="user-detail-pets-section">
                    <h3 className="user-detail-pets-title">{name}&apos;s Pets</h3>
                    {pets.length === 0 ? (
                        <p className="user-detail-no-pets">No pets added.</p>
                    ) : (
                        <div className="user-detail-pets-grid">
                            {pets.map((pet) => (
                                <div
                                    key={pet.id ?? pet.name}
                                    role="button"
                                    tabIndex={0}
                                    className="user-detail-pet-card user-detail-pet-card-clickable"
                                    onClick={() => {
                                        const id = pet.id ?? pet._id;
                                        if (id) navigate(`/admin/users/${userId}/pet/${id}`);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            const id = pet.id ?? pet._id;
                                            if (id) navigate(`/admin/users/${userId}/pet/${id}`);
                                        }
                                    }}
                                >
                                    <div className="user-detail-pet-avatar-wrap">
                                        <img
                                            src={(() => {
                                                const src = pet.image ?? pet.profileImage ?? pet.photo;
                                                if (!src) return window.getPublicUrl('/assets/images/dummy-profile.jpg');
                                                return src.startsWith('http') ? src : window.getPublicUrl(src);
                                            })()}
                                            alt={pet.name ?? 'Pet'}
                                            className="user-detail-pet-avatar"
                                            onError={(e) => { e.target.src = window.getPublicUrl('/assets/images/dummy-profile.jpg'); }}
                                        />
                                    </div>
                                    <div className="user-detail-pet-info">
                                        <h4 className="user-detail-pet-name">{pet.name ?? '-'}</h4>
                                        <PetDetailRow label="Gender" value={pet.gender} />
                                        <PetDetailRow label="Breed" value={pet.breed} />
                                        <PetDetailRow label="Age" value={pet.age ? `${pet.age} yrs old` : (pet.dateOfBirth ? `${pet.dateOfBirth}` : '-')} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UserDetail;
